import { Injectable, inject } from '@angular/core'
import { GoogleLoginResponse, SocialLogin } from '@capgo/capacitor-social-login'
import { Capacitor } from '@capacitor/core'
import { HttpClient } from '@angular/common/http'
import { ApiConfigService } from '@shared/services/api-config.service'
import { environment } from '../../../environments/environment'
import { CognitoService } from './cognito.service'
import { StoreSession } from 'src/app/store/store-session'
import { AuthService } from '@session/services/auth.service'
import { AppFlowService } from './app-flow.service'
import { User } from '@session/models/session'
import { PushNotificationsService } from './push-notifications.service'
import { ToastService } from './toast.service'
import { SessionActionsService } from './session-actions.service'
import { GoogleSigninResponse } from '@shared/models/socialPlugin'
import { CrashlyticsService } from './crashlithycs.service'
import { AlertController } from '@ionic/angular'

@Injectable({
  providedIn: 'root',
})
export class SocialPluginService {
  private readonly http = inject(HttpClient)
  private apiConfig = inject(ApiConfigService)
  private cognitoService = inject(CognitoService)
  readonly store = inject(StoreSession)
  private readonly authService = inject(AuthService)
  private readonly appFlowService = inject(AppFlowService)
  private readonly pushNotificationsService = inject(PushNotificationsService)
  private readonly toastService = inject(ToastService)
  private readonly sessionActionService = inject(SessionActionsService)
  private readonly crashlyticsService = inject(CrashlyticsService)
  private readonly alertController = inject(AlertController)

  private initialized = false
  private isDialogOpen = false

  async initializeSocialLogin() {
    if (this.initialized) return

    try {
      await SocialLogin.initialize({
        google: {
          webClientId: environment.WEB_CLIENT_ID,
        },
      })

      this.initialized = true
      if (Capacitor.isNativePlatform()) {
        this.resetAuthentication()
      }
    } catch (error) {
      console.error('Error al inicializar SocialLogin:', error)
      this.initialized = false
    }
  }

  private validateIdTokenWithBackend(idToken: string) {
    this.http.post<GoogleSigninResponse>(`${this.apiConfig.API_GOOGLE_VALIDATE}`, { idToken }).subscribe({
      next: async (resp: GoogleSigninResponse) => {
        this.saveAmplifyCompatibleTokens(resp)
        const accessTokenCognito: string = await this.cognitoService.returnAccesToken()
        this.store.changeAccesToken(accessTokenCognito)
        this.authService.login().subscribe({
          next: (resp: User) => {
            this.store.updateUser(resp)
            this.appFlowService.getStateApp().subscribe()
            this.pushNotificationsService.registerDevice()
            this.isDialogOpen = false
          },
          error: error => {
            this.isDialogOpen = false
            if (error.status == 403) {
              if (error.error.errorCodeMessage == 'DUROTAN_API_SER005') {
                this.toastService.showToast('Ya existe un cliente usando este correo electrónico')
                this.sessionActionService.logOut(false, false)
              }
            }
          },
        })
      },
      error: () => {
        this.googleNativeAlert()
        this.isDialogOpen = false
      },
    })
  }

  private async resetAuthentication() {
    try {
      const result = await SocialLogin.isLoggedIn({ provider: 'google' })

      if (result.isLoggedIn) {
        try {
          await SocialLogin.logout({ provider: 'google' })
        } catch (error) {
          this.crashlyticsService.log('Ocurrio un error al cerrar sesión con Google Native: ' + JSON.stringify(error))
        }
      }
    } catch (error) {
      this.crashlyticsService.log('Ocurrio un error al verificar estado de autenticación: ' + JSON.stringify(error))
    }
  }

  private saveAmplifyCompatibleTokens(tokens: GoogleSigninResponse) {
    const AMPLIFY_KEY = 'CognitoIdentityServiceProvider'
    const clientId = environment.USER_POOL_CLIENT_ID
    const amplifyTokenStructure = {
      [`${AMPLIFY_KEY}.${clientId}.${tokens.username}.accessToken`]: tokens.accessToken,
      [`${AMPLIFY_KEY}.${clientId}.${tokens.username}.idToken`]: tokens.idToken,
      [`${AMPLIFY_KEY}.${clientId}.${tokens.username}.refreshToken`]: tokens.refreshToken,
      [`${AMPLIFY_KEY}.${clientId}.${tokens.username}.tokenScopesString`]: 'openid email profile',
      [`${AMPLIFY_KEY}.${clientId}.LastAuthUser`]: tokens.username,
      [`${AMPLIFY_KEY}.${clientId}.${tokens.username}.userData`]: JSON.stringify(tokens.userInfo),
    }

    Object.entries(amplifyTokenStructure).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, value)
      }
    })
  }

  async loginWithGoogle() {
    if (this.isDialogOpen) return
    this.isDialogOpen = true
    type googleResult = GoogleLoginResponse & { idToken: string }
    try {
      const result = await SocialLogin.login({
        provider: 'google',
        options: {},
      })

      this.validateIdTokenWithBackend((result.result as googleResult).idToken)
    } catch (error) {
      let errorMessage = ''
      if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message?: string }).message || errorMessage
      }
      if (errorMessage.includes('[16]')) {
        //Customer cancel the login
      } else {
        this.crashlyticsService.log('Ocurrio un error al iniciar sesión con Google Native: ' + JSON.stringify(error))
      }
      this.isDialogOpen = false
    }
  }

  public async googleNativeAlert() {
    const alert = await this.alertController.create({
      header: 'No se pudo iniciar sesión',
      message: 'Hubo un problema al conectarte con Google. Intenta nuevamente.',
      buttons: [
        {
          text: 'Entendido',
          role: 'cancel',
        },
      ],
    })

    await alert.present()
  }
}
